
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase/client';
import { CalendarDots } from '@phosphor-icons/react';
import { DashboardWidget, WidgetLaedt } from './DashboardWidget';

interface TerminTag {
  label: string;
  dateStr: string;
  isToday: boolean;
  termine: { id: string; titel: string; typ: string; zeit: string }[];
}

const TYP_COLORS: Record<string, string> = {
  'besichtigung': 'var(--dw-typ-besichtigung)',
  'wartung': 'var(--dw-typ-wartung)',
  'frist': 'var(--dw-typ-frist)',
  'meeting': 'var(--dw-typ-meeting)',
};

interface TermineWocheWidgetProps {
  onNavigate?: (path: string) => void;
}

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const TermineWocheWidget: React.FC<TermineWocheWidgetProps> = ({ onNavigate }) => {
  const [tage, setTage] = useState<TerminTag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const now = new Date();
      const day = now.getDay();
      const diff = (day + 6) % 7;
      const monday = new Date(now);
      monday.setDate(now.getDate() - diff);
      monday.setHours(0, 0, 0, 0);

      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      const { data: termine } = await supabase
        .from('termine')
        .select('id, titel, typ, start_datum, status')
        .gte('start_datum', monday.toISOString())
        .not('status', 'eq', 'abgesagt')
        .order('start_datum', { ascending: true });

      const woche: TerminTag[] = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday);
        d.setDate(monday.getDate() + i);
        const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        const dayTermine = (termine || [])
          .filter(t => t.start_datum.substring(0, 10) === ds)
          .map(t => ({
            id: t.id,
            titel: t.titel,
            typ: t.typ,
            zeit: new Date(t.start_datum).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }),
          }));

        woche.push({
          label: TAGE[i],
          dateStr: ds,
          isToday: ds === todayStr,
          termine: dayTermine,
        });
      }

      setTage(woche);
    } catch (err) {
      console.error('Error fetching week termine:', err);
    } finally {
      setLoading(false);
    }
  };

  const totalTermine = tage.reduce((s, t) => s + t.termine.length, 0);

  const heute = new Date();
  const heuteKurz = TAGE[(heute.getDay() + 6) % 7].toUpperCase();

  return (
    <DashboardWidget farbe="--dw-rot" icon={CalendarDots} ariaLabel="Diese Woche" radius={28}
      onClick={() => onNavigate?.('/admin/kalender')} tip="Diese Woche – Termine Montag bis Sonntag, zum Kalender"
      style={{ padding: '14px 16px' }}>
      <div className="flex items-center" style={{ gap: 10 }}>
        {/* Kalenderblatt mit dem heutigen Tag */}
        <span aria-hidden className="flex shrink-0 flex-col items-center justify-center"
          style={{ width: 34, height: 34, borderRadius: 10, gap: 1, background: 'var(--dw-blatt)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.55), inset 0 0 0 0.5px rgba(0,0,0,0.12), 0 8px 16px -8px rgba(0,0,0,0.45)' }}>
          <span style={{ fontSize: 6, fontWeight: 800, letterSpacing: '0.06em', lineHeight: 1, color: 'var(--dw-blatt-tag)' }}>{heuteKurz}</span>
          <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-0.04em', lineHeight: 1, color: 'var(--dw-blatt-zahl)' }}>{heute.getDate()}</span>
        </span>
        <div className="flex min-w-0 flex-col">
          <h3 className="m-0" style={{ fontSize: 14, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink)' }}>Diese Woche</h3>
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{totalTermine} Termine</span>
        </div>
      </div>

      {loading ? (
        <WidgetLaedt />
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 4 }}>
          {tage.map(tag => (
            <div key={tag.dateStr} className="flex flex-col items-center"
              style={{ gap: 5, padding: '6px 0 8px', borderRadius: 14, background: tag.isToday ? 'var(--acc-soft)' : 'transparent' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: tag.isToday ? 'var(--acc)' : 'var(--ink3)' }}>{tag.label}</span>
              <span className="flex items-center justify-center tabular-nums"
                style={{ width: 26, height: 26, borderRadius: '50%', fontSize: 14, fontWeight: 800,
                  background: tag.isToday ? 'var(--acc-fill)' : 'transparent', color: tag.isToday ? 'var(--acc-ink)' : 'var(--ink)' }}>
                {parseInt(tag.dateStr.split('-')[2])}
              </span>
              <div className="flex items-center" style={{ height: 8, gap: 3 }}>
                {tag.termine.slice(0, 3).map(t => (
                  <span key={t.id} data-tip={`${t.zeit} – ${t.titel}`}
                    style={{ width: 6, height: 6, borderRadius: '50%', background: TYP_COLORS[t.typ] || 'var(--dw-typ-sonst)' }} />
                ))}
                {tag.termine.length > 3 && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: 'var(--ink3)' }}>+{tag.termine.length - 3}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardWidget>
  );
};

export default TermineWocheWidget;
